import type { FieldError } from 'react-hook-form';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useReactForm } from '../../../../../../hooks/index';
import { PopupType } from '../../../enums/popup-type.const';
import {
  ExpirationPeriod,
  TemplateCategory,
  TemplateSubCategory,
} from '../../../enums/template.const';
import { useStepThreeSubmitState } from '../../../hooks/use-step-three-submit-state.hook';
import { useTemplateDocument } from '../../../hooks/use-template-document.hook';
import { useTemplateFormResets } from '../../../hooks/use-template-form-resets.hook';
import { TemplateFormStore } from '../../../store/template-form.store';
import { TemplatePopupStore } from '../../../store/template-popup.store';
import { TemplateStep } from '../../../types/template.types';
import { hasFile } from '../../../utils/has-file.util';
import {
  getStepThreeFile,
  getStepThreeFileLink,
} from '../../../validations/add-template.validation';
import {
  getUpdateTemplateSchemaForStep,
  type UpdateTemplateFormSchema,
} from '../../../validations/edit-template.validation';
import { EditTemplateStepsView } from './edit-template-steps-view';
import { EditTemplateSummaryView } from './edit-template-summary-view';

export type EditTemplateFormProps = {
  data: {
    document: {
      category: TemplateCategory;
      documentName: string;
      equityPlanLabel: string;
      expirationPeriod: ExpirationPeriod | null;
      isSignOrderMatters: boolean;
      signatoriesLabel: string;
      signatoriesOrderLines: string[];
      signingModeLabel: string;
      subCategory: TemplateSubCategory;
    };
    fieldMappings: { field: string; placeholder: string }[];
    file: { documentLink: string; documentName: string; mappedFields: number };
  };
  defaultValues: UpdateTemplateFormSchema;
  fileLink: string;
  templateId: string;
};

export const EditTemplateForm: FC<EditTemplateFormProps> = ({
  data,
  defaultValues,
  fileLink,
  templateId,
}) => {
  const toggleState = TemplateFormStore(({ toggleState }) => toggleState);
  const currentStep = TemplateFormStore(({ currentStep }) => currentStep);
  const setStep = TemplateFormStore(({ setStep }) => setStep);
  const openPopup = TemplatePopupStore(({ openPopup }) => openPopup);
  const popupOpen = TemplatePopupStore(({ open }) => open);
  const popupType = TemplatePopupStore(({ type }) => type);

  const [editView, setEditView] = useState<'steps' | 'summary'>('summary');
  const hasHydratedStepThreeFileRef = useRef(false);

  const isMapFieldsModalOpen = popupOpen && popupType === PopupType.MAP_FIELDS;
  const isStepThreeOrMapModal = isMapFieldsModalOpen || (editView === 'steps' && currentStep === 3);
  const { file: templateFile, isPending: isTemplateFilePending } = useTemplateDocument({
    enabled: !!templateId && isStepThreeOrMapModal,
    fileLink,
    templateId,
  });

  const schema = useMemo(() => getUpdateTemplateSchemaForStep(currentStep), [currentStep]);

  const {
    clearErrors,
    control,
    formState: { errors, isDirty, isValid },
    getValues,
    setError,
    setValue,
    trigger,
    watch,
  } = useReactForm({ defaultValues, schema });

  useTemplateFormResets({ defaultValues, setStep, setValue, watch });

  useEffect(() => {
    hasHydratedStepThreeFileRef.current = false;
  }, [templateId]);

  useEffect(() => {
    if (!isStepThreeOrMapModal) return;
    const hasFileLink = (fileLink?.trim?.() ?? '') !== '';
    if (hasFileLink && isTemplateFilePending) return;

    const currentFile = getValues('stepThree.file');
    const currentLink = getStepThreeFileLink(currentFile);
    const hasUserFileNotMatchingTemplate = hasFile(currentFile) && currentLink !== fileLink;

    if (hasUserFileNotMatchingTemplate) {
      hasHydratedStepThreeFileRef.current = true;
      return;
    }

    if (hasHydratedStepThreeFileRef.current) return;

    if (templateFile) {
      setValue('stepThree.file', { doc: templateFile, docLink: fileLink });
      clearErrors('stepThree.file');
    } else {
      setValue('stepThree.file', undefined);
    }
    hasHydratedStepThreeFileRef.current = true;
    void trigger();
  }, [
    clearErrors,
    fileLink,
    getValues,
    isStepThreeOrMapModal,
    isTemplateFilePending,
    setValue,
    templateFile,
    trigger,
  ]);

  useEffect(() => {
    void trigger();
  }, [currentStep, trigger]);

  const { isNextOrSubmitDisabled } = useStepThreeSubmitState({
    control,
    currentStep,
    isDirty,
    isValid,
    mode: 'edit',
    templateFileLink: fileLink,
  });

  const handleClose = useCallback(() => {
    setEditView('summary');
    toggleState(false);
  }, [toggleState]);

  const handleSaveClick = useCallback(() => {
    const form = getValues();
    const currentStepThreeFile = form.stepThree?.file;
    const currentFileLink = getStepThreeFileLink(currentStepThreeFile) || fileLink;
    const currentFile = getStepThreeFile(currentStepThreeFile);
    const currentDocumentName = currentFile?.name || data.file.documentName;

    openPopup({
      metadata: templateId,
      onAcceptCallback: () => {},
      type: PopupType.UPDATE_TEMPLATE,
      updatePayload: {
        documentName: currentDocumentName,
        fileLink: currentFileLink,
        form,
      },
    });
    toggleState(false);
  }, [data.file.documentName, fileLink, getValues, openPopup, templateId, toggleState]);

  const goToSteps = useCallback(
    (step: TemplateStep) => {
      if (step === 3) {
        hasHydratedStepThreeFileRef.current = false;
      }
      setEditView('steps');
      setStep(step);
    },
    [setStep],
  );

  if (editView === 'steps') {
    return (
      <EditTemplateStepsView
        clearErrors={clearErrors}
        control={control}
        getFieldError={() => (errors?.stepOne as { name?: FieldError } | undefined)?.name}
        getValues={getValues}
        handleClose={handleClose}
        handleSaveClick={handleSaveClick}
        isDirty={isDirty}
        isNextOrSubmitDisabled={isNextOrSubmitDisabled}
        isValid={isValid}
        setError={setError}
        setValue={setValue}
        templateId={templateId}
      />
    );
  }

  return (
    <EditTemplateSummaryView
      control={control}
      data={data}
      getValues={getValues}
      goToSteps={goToSteps}
      handleClose={handleClose}
      handleSaveClick={handleSaveClick}
      isDirty={isDirty}
      isValid={isValid}
      setValue={setValue}
    />
  );
};
